import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-view-photo-modal',
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule],
  template: `
    <ion-content fullscreen class="photo-modal" [scrollY]="false">
      <div class="photo-backdrop" (click)="onBackdropClick()"></div>
      <div
        #viewport
        class="photo-wrapper"
        [class.is-zoomed]="scale > 1"
        [class.is-panning]="panning"
        (pointerdown)="onPointerDown($event)"
        (pointermove)="onPointerMove($event)"
        (pointerup)="onPointerUp($event)"
        (pointercancel)="onPointerUp($event)"
        (dblclick)="onDoubleClick($event)"
      >
        <button
          class="close-button"
          [attr.aria-label]="'ACTIONS.CLOSE' | translate"
          (click)="close(); stopEvent($event)"
        >
          &times;
        </button>
        <img
          [src]="imageUrl"
          [style.transform]="imageTransform"
          alt=""
          draggable="false"
        />
      </div>
    </ion-content>
  `,
  styles: [
    `
      ion-content.photo-modal {
        --background: rgba(0, 0, 0, 0.95);
      }

      .photo-backdrop {
        position: absolute;
        inset: 0;
        cursor: pointer;
      }

      .photo-wrapper {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        z-index: 1;
        padding: 1rem;
        overflow: hidden;
        touch-action: none;
        user-select: none;
        cursor: zoom-in;
      }

      .photo-wrapper.is-zoomed {
        cursor: grab;
      }

      .photo-wrapper.is-panning {
        cursor: grabbing;
      }

      .close-button {
        position: absolute;
        top: 16px;
        inset-inline-end: 16px;
        z-index: 2;
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        font-size: 1.5rem;
        line-height: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s ease;
      }

      .close-button:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      img {
        max-width: 90%;
        max-height: 90%;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
        transform-origin: center center;
        will-change: transform;
        pointer-events: none;
      }
    `,
  ],
})
export class ViewPhotoModalComponent implements AfterViewInit, OnDestroy {
  @Input() imageUrl!: string;
  @ViewChild('viewport') viewport?: ElementRef<HTMLElement>;

  scale = 1;
  translateX = 0;
  translateY = 0;
  panning = false;

  private dismissing = false;
  private readonly minScale = 1;
  private readonly maxScale = 4;
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchStartDistance = 0;
  private pinchStartScale = 1;
  private panStartX = 0;
  private panStartY = 0;
  private panOriginX = 0;
  private panOriginY = 0;
  private moved = false;
  private lastTapAt = 0;
  private lastTapX = 0;
  private lastTapY = 0;
  private onWheel = (event: WheelEvent) => this.handleWheel(event);

  constructor(private modalCtrl: ModalController) {}

  get imageTransform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  ngAfterViewInit() {
    this.viewport?.nativeElement.addEventListener('wheel', this.onWheel, {
      passive: false,
    });
  }

  ngOnDestroy() {
    this.viewport?.nativeElement.removeEventListener('wheel', this.onWheel);
  }

  close() {
    if (this.dismissing) return;

    this.dismissing = true;
    this.modalCtrl.dismiss();
  }

  stopEvent(event: Event) {
    event.stopPropagation();
  }

  onBackdropClick() {
    if (this.moved) return;
    this.close();
  }

  onDoubleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (this.scale > 1) {
      this.resetZoom();
      return;
    }

    this.zoomAt(event.clientX, event.clientY, 2.2 / this.scale);
  }

  onPointerDown(event: PointerEvent) {
    if ((event.target as HTMLElement).closest('.close-button')) return;

    event.preventDefault();
    this.viewport?.nativeElement.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.moved = false;

    if (this.pointers.size === 1 && this.scale > 1) {
      this.panning = true;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      this.panOriginX = this.translateX;
      this.panOriginY = this.translateY;
    }

    if (this.pointers.size === 2) {
      this.panning = false;
      this.pinchStartDistance = this.pointerDistance();
      this.pinchStartScale = this.scale;
    }
  }

  onPointerMove(event: PointerEvent) {
    if (!this.pointers.has(event.pointerId)) return;

    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 2) {
      const distance = this.pointerDistance();
      if (!this.pinchStartDistance) return;

      const factor = distance / this.pinchStartDistance;
      const center = this.pointerCenter();
      this.zoomTo(center.x, center.y, this.pinchStartScale * factor);
      this.moved = true;
      return;
    }

    if (this.panning && this.scale > 1) {
      const dx = event.clientX - this.panStartX;
      const dy = event.clientY - this.panStartY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.moved = true;
      this.translateX = this.panOriginX + dx;
      this.translateY = this.panOriginY + dy;
      this.clampPan();
    }
  }

  onPointerUp(event: PointerEvent) {
    const wasTap =
      !this.moved &&
      this.pointers.size <= 1 &&
      !this.panning &&
      this.scale === 1;
    this.pointers.delete(event.pointerId);

    if (this.pointers.size < 2) {
      this.pinchStartDistance = 0;
    }

    if (this.pointers.size === 1 && this.scale > 1) {
      const remaining = [...this.pointers.values()][0];
      this.panning = true;
      this.panStartX = remaining.x;
      this.panStartY = remaining.y;
      this.panOriginX = this.translateX;
      this.panOriginY = this.translateY;
      return;
    }

    if (this.pointers.size === 0) {
      this.panning = false;
      if (this.scale === this.minScale) this.resetZoom();

      if (wasTap && event.pointerType !== 'mouse') {
        const now = Date.now();
        const dx = event.clientX - this.lastTapX;
        const dy = event.clientY - this.lastTapY;
        const isDoubleTap =
          now - this.lastTapAt < 280 && Math.hypot(dx, dy) < 24;

        if (isDoubleTap) {
          this.zoomAt(event.clientX, event.clientY, 2.2);
          this.lastTapAt = 0;
        } else {
          this.lastTapAt = now;
          this.lastTapX = event.clientX;
          this.lastTapY = event.clientY;
        }
      }
    }
  }

  private handleWheel(event: WheelEvent) {
    event.preventDefault();
    event.stopPropagation();

    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.zoomAt(event.clientX, event.clientY, factor);
  }

  private zoomAt(clientX: number, clientY: number, factor: number) {
    this.zoomTo(clientX, clientY, this.scale * factor);
  }

  private zoomTo(clientX: number, clientY: number, nextScale: number) {
    const newScale = Math.min(
      this.maxScale,
      Math.max(this.minScale, nextScale),
    );
    const rect = this.viewport?.nativeElement.getBoundingClientRect();
    if (!rect) {
      this.scale = newScale;
      return;
    }

    const cx = clientX - rect.left - rect.width / 2;
    const cy = clientY - rect.top - rect.height / 2;
    const ratio = newScale / this.scale;

    this.translateX = cx - (cx - this.translateX) * ratio;
    this.translateY = cy - (cy - this.translateY) * ratio;
    this.scale = newScale;

    if (this.scale === this.minScale) {
      this.resetZoom();
      return;
    }

    this.clampPan();
  }

  private resetZoom() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.panning = false;
  }

  private clampPan() {
    if (this.scale <= 1) {
      this.translateX = 0;
      this.translateY = 0;
      return;
    }

    const viewport = this.viewport?.nativeElement;
    if (!viewport) return;

    const maxX = (viewport.clientWidth * (this.scale - 1)) / 2 + 48;
    const maxY = (viewport.clientHeight * (this.scale - 1)) / 2 + 48;
    this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
    this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
  }

  private pointerDistance(): number {
    const points = [...this.pointers.values()];
    if (points.length < 2) return 0;
    return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
  }

  private pointerCenter(): { x: number; y: number } {
    const points = [...this.pointers.values()];
    return {
      x: (points[0].x + points[1].x) / 2,
      y: (points[0].y + points[1].y) / 2,
    };
  }
}
